import React, { useState, useEffect } from "react";
import { hermesApi } from "../lib/api";
import { toast } from "sonner";
import {
  Lightbulb, ArrowRight, X, ArrowClockwise,
  Warning, ArrowUp, Minus, PaperPlaneRight,
} from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const PRIORITY_META = {
  urgent: { label: "Urgent",  icon: Warning,  badge: "bg-rose-50 text-rose-700 border-rose-200",   bar: "bg-rose-500" },
  high:   { label: "High",    icon: ArrowUp,  badge: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-500" },
  normal: { label: "Normal",  icon: Minus,    badge: "bg-slate-100 text-slate-600 border-slate-200",bar: "bg-slate-300" },
};

function SuggestionCard({ suggestion, onQueue, onDismiss }) {
  const [loading, setLoading] = useState(false);
  const meta = PRIORITY_META[suggestion.priority] || PRIORITY_META.normal;
  const Icon = meta.icon;
  const isQueued    = suggestion.status === "queued";
  const isDismissed = suggestion.status === "dismissed";

  const queue = async () => {
    setLoading(true);
    try {
      await onQueue(suggestion);
    } finally {
      setLoading(false);
    }
  };

  return (
    <article className={`border rounded-lg bg-white transition-all ${isDismissed ? "opacity-40" : "hover:shadow-sm"}`}>
      {/* Priority bar */}
      <div className={`h-0.5 w-full rounded-t-lg ${meta.bar}`} />
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold tracking-wider px-2 py-0.5 rounded border ${meta.badge}`}>
              <Icon size={11} />
              {meta.label.toUpperCase()}
            </span>
            <span className="text-[11px] font-mono text-slate-400">
              {new Date(suggestion.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
            </span>
          </div>
          {!isDismissed && (
            <button
              onClick={() => onDismiss(suggestion.id)}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
              title="Dismiss"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Prompt */}
        <p className="text-[15px] font-semibold text-slate-900 leading-snug mb-2">
          {suggestion.prompt}
        </p>

        {/* Rationale */}
        {suggestion.rationale && (
          <p className="text-[13px] text-slate-500 leading-relaxed mb-4">
            {suggestion.rationale}
          </p>
        )}

        {/* Action */}
        {!isDismissed && (
          <div className="flex items-center justify-end">
            {isQueued ? (
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-emerald-600">
                <PaperPlaneRight size={14} weight="fill" /> Research queued
              </span>
            ) : (
              <Button size="sm" onClick={queue} disabled={loading}>
                <ArrowRight size={14} />
                {loading ? "Queuing…" : "Research this"}
              </Button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default function SuggestionsPage() {
  const [suggestions,     setSuggestions]     = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [refreshing,      setRefreshing]      = useState(false);
  const [showDismissed,   setShowDismissed]   = useState(false);

  const load = async (withDismissed = showDismissed) => {
    setLoading(true);
    try {
      const { data } = await hermesApi.suggestions.list(
        withDismissed ? { include_dismissed: true } : {}
      );
      setSuggestions(data);
    } catch { toast.error("Failed to load suggestions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const requestRefresh = async () => {
    setRefreshing(true);
    try {
      await hermesApi.tasks.create({
        prompt: "__generate_suggestions__ Review the current intelligence database, identify research gaps, and generate 8 high-value research prompts for the operator. For each: write the prompt as a clear research question, explain why it matters now (rationale), and set priority (urgent/high/normal). Return as JSON array [{prompt, rationale, priority}].",
      });
      toast.success("Suggestion refresh queued for next Hermes cycle");
    } catch { toast.error("Failed to queue refresh"); }
    finally { setRefreshing(false); }
  };

  const queueSuggestion = async (suggestion) => {
    try {
      await hermesApi.tasks.create({ prompt: suggestion.prompt });
      const { data: updated } = await hermesApi.suggestions.update(suggestion.id, { status: "queued" });
      setSuggestions((s) => s.map((x) => x.id === suggestion.id ? updated : x));
      toast.success("Research queued");
    } catch { toast.error("Failed to queue"); }
  };

  const dismissSuggestion = async (id) => {
    try {
      const { data: updated } = await hermesApi.suggestions.update(id, { status: "dismissed" });
      setSuggestions((s) => s.map((x) => x.id === id ? updated : x));
    } catch { toast.error("Failed to dismiss"); }
  };

  const pending   = suggestions.filter((s) => s.status === "pending");
  const queued    = suggestions.filter((s) => s.status === "queued");
  const dismissed = suggestions.filter((s) => s.status === "dismissed");

  const lastGenerated = suggestions[0]?.created_at
    ? new Date(suggestions[0].created_at)
    : null;

  const hoursAgo = lastGenerated
    ? Math.round((Date.now() - lastGenerated.getTime()) / 3600000)
    : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">
            Intelligence
          </p>
          <h1 className="font-display text-[22px] font-bold text-slate-900 tracking-tight">
            Suggested Topics
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Hermes-identified research gaps based on current intelligence and industry signals.
            {hoursAgo !== null && (
              <span className="ml-2 text-slate-400">
                Last generated {hoursAgo < 1 ? "recently" : `${hoursAgo}h ago`}.
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={requestRefresh} disabled={refreshing}>
          <ArrowClockwise size={13} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Queuing…" : "Request refresh"}
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[13px] text-slate-400">Loading suggestions…</div>
      ) : pending.length === 0 && queued.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-slate-200 rounded-xl">
          <Lightbulb size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-[15px] font-medium text-slate-500 mb-1">No suggestions yet</p>
          <p className="text-[13px] text-slate-400 mb-4">
            Hermes generates suggestions at the end of each intelligence cycle.<br />
            Request a refresh to trigger one now.
          </p>
          <Button size="sm" onClick={requestRefresh} disabled={refreshing}>
            <ArrowClockwise size={13} className={refreshing ? "animate-spin" : ""} />
            Request now
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pending */}
          {pending.length > 0 && (
            <div className="space-y-3">
              {pending.map((s) => (
                <SuggestionCard
                  key={s.id}
                  suggestion={s}
                  onQueue={queueSuggestion}
                  onDismiss={dismissSuggestion}
                />
              ))}
            </div>
          )}

          {/* Queued */}
          {queued.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">
                Queued ({queued.length})
              </p>
              <div className="space-y-3">
                {queued.map((s) => (
                  <SuggestionCard
                    key={s.id}
                    suggestion={s}
                    onQueue={queueSuggestion}
                    onDismiss={dismissSuggestion}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Dismissed toggle */}
          {dismissed.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => {
                  const next = !showDismissed;
                  setShowDismissed(next);
                  load(next);
                }}
                className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showDismissed ? "Hide dismissed" : `Show ${dismissed.length} dismissed`}
              </button>
              {showDismissed && (
                <div className="space-y-3 mt-3">
                  {dismissed.map((s) => (
                    <SuggestionCard
                      key={s.id}
                      suggestion={s}
                      onQueue={queueSuggestion}
                      onDismiss={dismissSuggestion}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
